import { Fragment } from "react/jsx-runtime";
import Styles from "../../styles/report/category.module.scss";
import Reportbutton from "./Reportbutton";

const Category = ({ summary ,clientId }) => {
  // Helper function to safely get workout titles
  const getWorkoutTitle = (index) => {
    return summary?.workouts?.[index]?.workout?.name || "";
  };

  const titles = {
    Category1: getWorkoutTitle(0),
    Category2: getWorkoutTitle(1),
    Category3: getWorkoutTitle(2),
    Category4: getWorkoutTitle(3),
    Category5: getWorkoutTitle(4),
    Category6: getWorkoutTitle(5),
  };

  // Create category data with proper validation
  const createCategoryData = (workoutIndex) => {
    const topPerformers =
      summary?.workouts?.[workoutIndex]?.topPerformers || [];
    const categoryData = [];

    for (let i = 0; i < 7; i++) {
      const performer = topPerformers[i];
      if (
        performer?.client?.name &&
        performer?.score !== undefined &&
        performer?.score !== null
      ) {
        categoryData.push({
          name:
            performer.client.name.length > 5
              ? performer.client.name.slice(0, 4) + ".."
              : performer.client.name,
          value: performer.score,
          hasData: true,
        });
      } else {
        categoryData.push({
          name: "",
          value: 0,
          hasData: false,
        });
      }
    }

    return categoryData;
  };

  // Create gender-based data with proper validation (no name trimming)
  const createGenderData = (gender) => {
    const performers = summary?.topPerformers?.[gender] || [];
    const genderData = [];

    for (let i = 0; i < 5; i++) {
      const performer = performers[i];
      if (
        performer?.client?.name &&
        performer?.score !== undefined &&
        performer?.score !== null
      ) {
        genderData.push({
          name:
            performer.client.name.length > 5
              ? performer.client.name.slice(0, 4) + ".."
              : performer.client.name,
          value: Math.round(performer?.score),

          hasData: true,
        });
      } else {
        genderData.push({
          name: "",
          value: 0,
          hasData: false,
        });
      }
    }

    return genderData;
  };

  // Generate category data
  const categoryData = [
    createCategoryData(0),
    createCategoryData(1),
    createCategoryData(2),
    createCategoryData(3),
    createCategoryData(4),
    createCategoryData(5),
  ];

  // Generate gender data
  const femaleData = createGenderData("female");
  const maleData = createGenderData("male");

  const maxValue = 800; // Changed to match the highest y-axis label
  const maxHorizontalValue = 100;
  const yAxisLabels = [800, 700, 600, 500, 400, 300, 200, 100, 0];
  const xAxisLabels = [0, 20, 40, 60, 80, 100];

  const renderChart = (data, title) => {
    // Don't render chart if no title
    if (!title) {
      return null;
    }

    return (
      <div className={Styles.chartContainer}>
        <h3 className={Styles.chartTitle}>
          <span className={Styles.titlespace}>{title}</span>
        </h3>
        <div className={Styles.chart}>
          <div className={Styles.yAxis}>
            {yAxisLabels.map((label, index) => (
              <div key={index} className={Styles.yAxisLabel}>
                {label}
              </div>
            ))}
          </div>
          <div className={Styles.chartContent}>
            <div className={Styles.gridLines}>
              {/* FIXED: Render all grid lines to match y-axis labels */}
              {yAxisLabels.map((_, index) => (
                <div key={index} className={Styles.gridLine}></div>
              ))}
            </div>
            <div className={Styles.barsContainer}>
              {data.map((item, index) => (
                <div key={index} className={Styles.barWrapper}>
                  <div
                    className={Styles.bar}
                    style={{
                      height: item.hasData
                        ? `${(item.value / maxValue) * 100}%`
                        : "0%",
                      visibility: item.hasData ? "visible" : "hidden",
                    }}
                  ></div>
                  <div className={Styles.xAxisLabel}>
                    {item.hasData ? item.name : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHorizontalChart = (data, title) => {
    // Don't render chart if no valid data at all
    if (!data.some((item) => item.hasData)) {
      return null;
    }

    return (
      <div className={Styles.horizontalChartContainer}>
        <h3 className={Styles.chartTitle}>
          <span className={Styles.titlespace}>{title}</span>
        </h3>
        <div className={Styles.horizontalChart}>
          <div className={Styles.horizontalChartContent}>
            <div className={Styles.horizontalGridLines}>
              {/* FIXED: Render correct number of horizontal grid lines */}
              {xAxisLabels.slice(0).map((_, index) => (
                <div key={index} className={Styles.horizontalGridLine}></div>
              ))}
            </div>
            <div className={Styles.horizontalBarsContainer}>
              {data.map((item, index) => (
                <div key={index} className={Styles.horizontalBarWrapper}>
                  <div className={Styles.horizontalYAxisLabel}>
                    {item.hasData ? item.name : ""}
                  </div>
                  <div
                    className={Styles.horizontalBar}
                    style={{
                      width: item.hasData
                        ? `${(item.value / maxHorizontalValue) * 100}%`
                        : "0%",
                      visibility: item.hasData ? "visible" : "hidden",
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
          <div className={Styles.horizontalXAxis}>
            {xAxisLabels.map((label, index) => (
              <div key={index} className={Styles.horizontalXAxisLabel}>
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Group charts for rendering with responsive layout
  const chartPairs = [
    [categoryData[0], titles.Category1, categoryData[1], titles.Category2],
    [categoryData[2], titles.Category3, categoryData[3], titles.Category4],
    [categoryData[4], titles.Category5, categoryData[5], titles.Category6],
  ];

  return (
    <Fragment>
      {/* Render category chart pairs */}
      {chartPairs.map((pair, pairIndex) => (
        <div key={pairIndex} className={Styles.categoryContainer}>
          {renderChart(pair[0], pair[1])}
          {renderChart(pair[2], pair[3])}
        </div>
      ))}

      {/* Render gender performance charts */}
      <div className={Styles.categoryContainer}>
        {renderHorizontalChart(
          femaleData,
          "Top 5 Overall Performances - Female"
        )}
        {renderHorizontalChart(maleData, "Top 5 Overall Performances - Male")}
      </div>

      <Reportbutton clientId={clientId} />
    </Fragment>
  );
};

export default Category;
